import { Autocomplete as HeroUiAutocomplete, AutocompleteItem } from '@heroui/react';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { type AutocompleteOption } from './Autocomplete.types';

type Props = {
    options: AutocompleteOption[];
    value: string;
    onValueChange: (value: string) => void;
    'aria-label': string;
    className?: string;
    isDisabled?: boolean;
    placeholder?: string;
    // Compact text to show in the input for the current selection, where the full label is too long
    // for the space — a dialing code instead of "United States (USA) (+1)", say. The list still shows
    // the full label, and typing still searches it. Providing this also sizes the dropdown to its own
    // content, since a narrow input would otherwise clip the labels.
    selectedDisplayValue?: string;
    // Reports the field as required to assistive tech. The visible asterisk comes from the caller's
    // label, since this control has no visible label of its own.
    isRequired?: boolean;
};

// A single-value picker you can filter by typing, for lists too long to scan — country pickers and
// the like. Mirrors `Select`'s API (`options` / `value` / `onValueChange`), so a call site can move
// between them by changing the component name.
//
// Prefer `Select` for short lists: this renders a text input, which is noise when there are only a
// few options. Not to be confused with `FrontRoyalMaterialUiForm`'s MUI Autocomplete, used by admin.
//
// Like `Select`, this is a bare control: no label, no error message, and not form-aware, so in-form
// callers supply those and wrap it in a `Controller`.
export function Autocomplete({
    options,
    value,
    onValueChange,
    'aria-label': ariaLabel,
    className,
    isDisabled,
    placeholder,
    selectedDisplayValue,
    isRequired,
}: Props) {
    const selectedOption = options.find(option => option.value === value);
    const displayText = selectedDisplayValue ?? selectedOption?.label ?? '';

    // null means "show the current selection"; a string means the user is searching. Opening clears
    // it to '' so the whole list is browsable — otherwise the selected label filters the list down
    // to itself and there's no way to see the other options.
    const [searchText, setSearchText] = useState<string | null>(null);

    const isCompact = selectedDisplayValue !== undefined;

    return (
        <HeroUiAutocomplete
            aria-label={ariaLabel}
            className={className}
            isDisabled={isDisabled}
            isRequired={isRequired}
            placeholder={placeholder}
            selectedKey={value || null}
            // No clear button: the selection can't be cleared anyway (see onSelectionChange), so it
            // only misleads, and it eats room a compact input can't spare.
            isClearable={false}
            // Virtualized lists position their rows out of flow, which collapses the intrinsic width
            // the compact dropdown below depends on. Only matters when we're sizing to content.
            isVirtualized={isCompact ? false : undefined}
            // Only filter on text the user actually typed. The input also holds the current
            // selection's display text, and that text is itself a substring match — usually of
            // exactly one option — so without this the list collapses to a single row as the popover
            // closes after a selection, which reads as the dropdown flashing back up. (A `+1` code
            // matched many options, which is why that one code looked fine.)
            defaultFilter={(textValue, inputText) =>
                inputText === displayText || textValue.toLowerCase().includes(inputText.toLowerCase())
            }
            inputValue={searchText ?? displayText}
            onInputChange={setSearchText}
            onOpenChange={isOpen => setSearchText(isOpen ? '' : null)}
            // The selection only changes by choosing an option — emptying the text doesn't drop it,
            // which is what stops the blank-on-open above from wiping the value. Same as the native
            // select this replaces. The null branch is defensive.
            onSelectionChange={key => {
                setSearchText(null);
                onValueChange(key === null ? '' : String(key));
            }}
            // Styled to match the component-library Input, including its height, so the two sit
            // together in a form without a visible step between them.
            inputProps={{
                classNames: {
                    inputWrapper: 'bg-slate-grey-50 hover:bg-slate-grey-100 rounded-7 h-14 min-h-10',
                    input: 'text-slate-grey group-data-[has-value=true]:text-slate-grey text-base',
                },
            }}
            popoverProps={{
                classNames: {
                    content: twMerge(
                        'border border-solid border-slate-grey-200 shadow-smallish',
                        // HeroUI sizes the dropdown to the input. That's right when the input shows
                        // the full label, but a compact input would clip every option, so size to
                        // content instead — capped to the viewport so a long label can't overflow it.
                        // Important modifier because HeroUI's own `w-full` isn't in this twMerge.
                        isCompact && '!w-max max-w-[calc(100vw-2rem)]',
                    ),
                },
            }}
        >
            {options.map(option => (
                <AutocompleteItem key={option.value} textValue={option.label}>
                    {option.label}
                </AutocompleteItem>
            ))}
        </HeroUiAutocomplete>
    );
}
